import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { track } from "../lib/analytics";
import { EquipmentCard } from "./EquipmentCard";
import { usePublicCategories, usePublicTypeSearch } from "./api";
import type { CategorySummary, TypeSummary } from "./types";

// FR-5 — /search?q=… overflow page. Renders all matched types as
// EquipmentCards. Empty state ("No equipment matched 'X'") includes
// quick-jump cards for the first three categories.
export function SearchResultsPage(): JSX.Element {
  const [params] = useSearchParams();
  const query = useMemo(() => params.get("q")?.trim() ?? "", [params]);
  const categories = usePublicCategories();
  const { data, isPending, isError, error } = usePublicTypeSearch(query);

  useEffect(() => {
    if (query.length > 0) {
      track({
        name: "search_performed",
        query_length: query.length,
        result_count: data?.length ?? 0
      });
    }
    // Only fire on query change + initial settled load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, data?.length]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-black">
          {query.length > 0 ? `Results for “${query}”` : "Search"}
        </h1>
        <p className="mt-1 text-sm text-zinc-600">
          {query.length > 0
            ? "Showing equipment whose name or category matches your search."
            : "Use the header search to find equipment by name or category."}
        </p>
      </header>

      {query.length === 0 ? null : isPending ? (
        <SearchSkeleton />
      ) : isError ? (
        <Alert variant="destructive">
          Search failed. {error instanceof ApiError ? `(HTTP ${error.status})` : null}
        </Alert>
      ) : (data ?? []).length === 0 ? (
        <EmptyResults query={query} categories={categories.data ?? []} />
      ) : (
        <ResultsGrid types={data ?? []} />
      )}
    </div>
  );
}

function ResultsGrid({ types }: { types: TypeSummary[] }): JSX.Element {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {types.map((t) => (
        <li key={t.id}>
          {/* Category slug placeholder — TypeDetailPage rewrites the
              URL to the canonical /c/<actual-category>/<type> on load. */}
          <EquipmentCard type={t} categorySlug="_" />
        </li>
      ))}
    </ul>
  );
}

function EmptyResults({
  query,
  categories
}: {
  query: string;
  categories: CategorySummary[];
}): JSX.Element {
  const quickJump = categories.slice(0, 3);
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-8 text-center">
      <p className="text-base text-brand-black">
        No equipment matched &ldquo;{query}&rdquo;.
      </p>
      {quickJump.length > 0 ? (
        <>
          <p className="mt-2 text-sm text-zinc-600">Try one of these categories:</p>
          <ul className="mt-4 flex flex-wrap justify-center gap-2">
            {quickJump.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/c/${c.slug}`}
                  className="inline-flex items-center rounded-md border border-zinc-200 bg-brand-white px-3 py-2 text-sm text-brand-black hover:border-brand-red hover:text-brand-red"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-2 text-sm text-zinc-600">
          <Link to="/" className="text-brand-red hover:underline">Browse all categories →</Link>
        </p>
      )}
    </div>
  );
}

function SearchSkeleton(): JSX.Element {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-64" />
        </li>
      ))}
    </ul>
  );
}
